"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { analyzeDatePeriod } from "@/lib/date-period-utils";
import { subDays } from "date-fns";
import { useBranch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";

const EVALUATION_TYPES = [
  "overall_evaluation",
  "base_components",
  "extras",
  "removals",
  "pizza_saver_evaluation",
] as const;

type EvaluationType = (typeof EVALUATION_TYPES)[number];

type EvaluationCounts = {
  ok: number;
  nok: number;
  incomplete: number;
  ratio: number;
};

type BranchComparisonData = {
  branchName: string;
  branchId: string;
  totalPizzas: number;
  overall: number;
  ingredients: number;
  extras: number;
  removals: number;
  pizzaSaver: number;
};

type BranchComparisonAggregate = {
  _id?: unknown;
  totalPizzas?: unknown;
  overall_ok?: unknown;
  overall_nok?: unknown;
  overall_incomplete?: unknown;
  base_components_ok?: unknown;
  base_components_nok?: unknown;
  base_components_incomplete?: unknown;
  extras_ok?: unknown;
  extras_nok?: unknown;
  extras_incomplete?: unknown;
  removals_ok?: unknown;
  removals_nok?: unknown;
  removals_incomplete?: unknown;
  pizza_saver_ok?: unknown;
  pizza_saver_nok?: unknown;
  pizza_saver_incomplete?: unknown;
};

interface BranchComparisonBaseProps {
  className?: string;
  title: string;
  description?: string;
  selectedBranches?: string[];
  showAverage?: boolean;
  maxBranches?: number;
}

const readNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const readEvaluation = (
  result: BranchComparisonAggregate,
  keyPrefix: string,
): EvaluationCounts => {
  const ok = readNumber(result[`${keyPrefix}_ok` as keyof typeof result]);
  const nok = readNumber(result[`${keyPrefix}_nok` as keyof typeof result]);
  const incomplete = readNumber(
    result[`${keyPrefix}_incomplete` as keyof typeof result],
  );

  return {
    ok,
    nok,
    incomplete,
    ratio: ok + nok > 0 ? ok / (ok + nok) : 0,
  };
};

const getEvaluationKeyPrefix = (evalType: EvaluationType) => {
  if (evalType === "overall_evaluation") {
    return "overall";
  }

  if (evalType === "base_components") {
    return "base_components";
  }

  if (evalType === "pizza_saver_evaluation") {
    return "pizza_saver";
  }

  return evalType;
};

const getAverageRow = (rows: BranchComparisonData[], label: string) => {
  if (rows.length === 0) {
    return null;
  }

  const average = (key: keyof BranchComparisonData) =>
    Math.round(
      rows.reduce((sum, item) => sum + Number(item[key]), 0) / rows.length,
    );

  return {
    branchName: label,
    branchId: "average",
    totalPizzas: average("totalPizzas"),
    overall: average("overall"),
    ingredients: average("ingredients"),
    extras: average("extras"),
    removals: average("removals"),
    pizzaSaver: average("pizzaSaver"),
  };
};

export function BranchComparisonBase({
  className,
  title,
  description,
  selectedBranches,
  showAverage = false,
  maxBranches = 10,
}: BranchComparisonBaseProps) {
  const { processedDateRange } = useDashboardDateRange();
  const { start, end } = processedDateRange;
  const { branches } = useBranch();
  const { t, formatNumber, locale } = useLocalization();
  /** a value in percent (58 -> "58%" / "58 %") */
  const formatPercent = (value: number) =>
    formatNumber(value / 100, { style: "percent" });

  const chartConfig = useMemo<ChartConfig>(
    () => ({
      overall: {
        label: t("comparison.overall"),
        color: "hsl(var(--chart-1))",
      },
      ingredients: {
        label: t("comparison.ingredients"),
        color: "hsl(var(--chart-2))",
      },
      extras: {
        label: t("comparison.extras"),
        color: "hsl(var(--chart-3))",
      },
      removals: {
        label: t("comparison.removals"),
        color: "hsl(var(--chart-4))",
      },
      pizzaSaver: {
        label: t("comparison.pizzaSaver"),
        color: "hsl(var(--chart-5))",
      },
    }),
    [t],
  );

  const periodInfo = useMemo(() => {
    if (start && end) {
      return analyzeDatePeriod(start, end);
    }
    const endDate = new Date();
    const startDate = subDays(endDate, 7);
    return analyzeDatePeriod(startDate, endDate);
    // the period label follows the language
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, locale]);

  const [chartData, setChartData] = useState<BranchComparisonData[]>([]);
  const [averageData, setAverageData] =
    useState<BranchComparisonData | null>(null);
  const { mutateAsync, isPending: isLoading } = usePostAggregatePizzas({});

  const branchesToCompare = useMemo(() => {
    const allBranches = (branches ?? []).filter((branch) => branch.bb_branch_id);

    if (!selectedBranches?.length) {
      return allBranches;
    }

    return allBranches.filter(
      (branch) =>
        selectedBranches.includes(branch.id) ||
        selectedBranches.includes(branch.bb_branch_id),
    );
  }, [branches, selectedBranches]);

  useEffect(() => {
    if (!start || !end) return;

    const branchIds = branchesToCompare.map((branch) => branch.bb_branch_id);
    if (branchIds.length === 0) {
      setChartData([]);
      setAverageData(null);
      return;
    }

    let isMounted = true;

    const fetchBranchData = async () => {
      try {
        const response = await mutateAsync({
          data: {
            pipeline: [
              {
                $match: {
                  branch_id: { $in: branchIds },
                  "evaluation.metadata": { $exists: true },
                  capture_at: {
                    $gte: start.toISOString(),
                    $lte: end.toISOString(),
                  },
                },
              },
              {
                $project: {
                  branch_id: 1,
                  overall_evaluation:
                    "$evaluation.metadata.overall_evaluation.status",
                  base_components:
                    "$evaluation.metadata.components_evaluation.base_components.status",
                  extras: "$evaluation.metadata.components_evaluation.extras.status",
                  removals:
                    "$evaluation.metadata.components_evaluation.removals.status",
                  pizza_saver_evaluation:
                    "$evaluation.metadata.pizza_saver_evaluation.status",
                },
              },
              {
                $group: {
                  _id: "$branch_id",
                  totalPizzas: { $sum: 1 },
                  overall_ok: {
                    $sum: {
                      $cond: [{ $eq: ["$overall_evaluation", "OK"] }, 1, 0],
                    },
                  },
                  overall_nok: {
                    $sum: {
                      $cond: [{ $eq: ["$overall_evaluation", "NOK"] }, 1, 0],
                    },
                  },
                  overall_incomplete: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $ne: ["$overall_evaluation", "OK"] },
                            { $ne: ["$overall_evaluation", "NOK"] },
                          ],
                        },
                        1,
                        0,
                      ],
                    },
                  },
                  base_components_ok: {
                    $sum: {
                      $cond: [{ $eq: ["$base_components", "OK"] }, 1, 0],
                    },
                  },
                  base_components_nok: {
                    $sum: {
                      $cond: [{ $eq: ["$base_components", "NOK"] }, 1, 0],
                    },
                  },
                  base_components_incomplete: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $ne: ["$base_components", "OK"] },
                            { $ne: ["$base_components", "NOK"] },
                          ],
                        },
                        1,
                        0,
                      ],
                    },
                  },
                  extras_ok: {
                    $sum: { $cond: [{ $eq: ["$extras", "OK"] }, 1, 0] },
                  },
                  extras_nok: {
                    $sum: { $cond: [{ $eq: ["$extras", "NOK"] }, 1, 0] },
                  },
                  extras_incomplete: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $ne: ["$extras", "OK"] },
                            { $ne: ["$extras", "NOK"] },
                          ],
                        },
                        1,
                        0,
                      ],
                    },
                  },
                  removals_ok: {
                    $sum: { $cond: [{ $eq: ["$removals", "OK"] }, 1, 0] },
                  },
                  removals_nok: {
                    $sum: { $cond: [{ $eq: ["$removals", "NOK"] }, 1, 0] },
                  },
                  removals_incomplete: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $ne: ["$removals", "OK"] },
                            { $ne: ["$removals", "NOK"] },
                          ],
                        },
                        1,
                        0,
                      ],
                    },
                  },
                  pizza_saver_ok: {
                    $sum: {
                      $cond: [
                        { $eq: ["$pizza_saver_evaluation", "OK"] },
                        1,
                        0,
                      ],
                    },
                  },
                  pizza_saver_nok: {
                    $sum: {
                      $cond: [
                        { $eq: ["$pizza_saver_evaluation", "NOK"] },
                        1,
                        0,
                      ],
                    },
                  },
                  pizza_saver_incomplete: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $ne: ["$pizza_saver_evaluation", "OK"] },
                            { $ne: ["$pizza_saver_evaluation", "NOK"] },
                          ],
                        },
                        1,
                        0,
                      ],
                    },
                  },
                },
              },
            ],
          },
        });

        if (!isMounted) return;

        const branchById = new Map(
          branchesToCompare.map((branch) => [branch.bb_branch_id, branch]),
        );
        const rawResults =
          (response as { results?: BranchComparisonAggregate[] }).results ?? [];

        const allRows = rawResults
          .filter((result) => typeof result._id === "string")
          .map((result) => {
            const branch = branchById.get(result._id as string);
            if (!branch) {
              return null;
            }

            const evaluations = Object.fromEntries(
              EVALUATION_TYPES.map((evalType) => [
                evalType,
                readEvaluation(result, getEvaluationKeyPrefix(evalType)),
              ]),
            ) as Record<EvaluationType, EvaluationCounts>;

            return {
              branchName: branch.name,
              branchId: branch.bb_branch_id,
              totalPizzas: readNumber(result.totalPizzas),
              overall: Math.round(
                evaluations.overall_evaluation.ratio * 100,
              ),
              ingredients: Math.round(
                evaluations.base_components.ratio * 100,
              ),
              extras: Math.round(evaluations.extras.ratio * 100),
              removals: Math.round(evaluations.removals.ratio * 100),
              pizzaSaver: Math.round(
                evaluations.pizza_saver_evaluation.ratio * 100,
              ),
            };
          })
          .filter((row): row is BranchComparisonData => Boolean(row))
          .filter((row) => row.totalPizzas > 0)
          .sort((a, b) => b.overall - a.overall);

        setChartData(allRows.slice(0, maxBranches));
        setAverageData(
          showAverage ? getAverageRow(allRows, t("comparison.average")) : null,
        );
      } catch (error) {
        console.error("Error fetching branch comparison data:", error);
        if (isMounted) {
          setChartData([]);
          setAverageData(null);
        }
      }
    };

    void fetchBranchData();

    return () => {
      isMounted = false;
    };
  }, [
    start,
    end,
    branchesToCompare,
    maxBranches,
    mutateAsync,
    showAverage,
    t,
  ]);

  const displayData = useMemo(() => {
    if (showAverage && averageData) {
      return [...chartData, averageData];
    }
    return chartData;
  }, [averageData, chartData, showAverage]);

  const chartHeight = Math.max(300, displayData.length * 38);

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description ||
            t("comparison.defaultDescription", {
              period: periodInfo.periodLabel,
            })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {displayData.length === 0 && !isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("comparison.noData")}
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto w-full"
            style={{ height: chartHeight }}
          >
            <BarChart
              data={displayData}
              layout="vertical"
              margin={{ left: 120 }}
            >
              <CartesianGrid vertical={true} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(v) => formatPercent(Number(v))}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="branchName"
                tickLine={false}
                axisLine={false}
                width={120}
                interval={0}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelKey="branchName"
                    hideLabel={false}
                    valueFormatter={(value) => formatPercent(Number(value))}
                  />
                }
              />
              <Bar
                dataKey="overall"
                fill="var(--color-overall)"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="ingredients"
                fill="var(--color-ingredients)"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="extras"
                fill="var(--color-extras)"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="removals"
                fill="var(--color-removals)"
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="pizzaSaver"
                fill="var(--color-pizzaSaver)"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
        {isLoading && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
